import React, { useState } from 'react';
import { Button, Input, Form, useToast } from '@crisellevaldez/speculo-ui';
import { CheckCircle2 } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';
import { useClientOwner } from '../../../../hooks/useClientOwner';

interface CrashCourseData {
  meeting_link?: string;
  completed?: boolean;
  auto_populated_at?: string;
  completed_at?: string;
}

interface OnboardingDataShape {
  crash_course?: CrashCourseData;
  crashCourse?: CrashCourseData;
  [key: string]: unknown;
}

interface StrategyCallSectionProps {
  selectedOrg: Organization;
  onboardingSetup: unknown;
  onboardingData: OnboardingDataShape;
  asTab?: boolean;
  onRefresh?: () => void;
}

// Keep the export name for backwards compatibility but internally use Strategy Call naming
export const CrashCourseSection: React.FC<StrategyCallSectionProps> = ({
  selectedOrg,
  onboardingData,
  onRefresh,
}) => {
  const { showToast } = useToast();
  const { updateSettings, isPending } = useUpdateOrganizationSettings();
  const owner = useClientOwner();

  // Canonical shape: snake_case. Accept legacy camelCase `crashCourse` from
  // old Firestore onboarding so legacy orgs see correct status.
  const strategyCallData = onboardingData?.crash_course ?? onboardingData?.crashCourse;
  const strategyMeetingLink = strategyCallData?.meeting_link;
  const strategyAutoPopulatedAt = strategyCallData?.auto_populated_at;
  const baseOnboardingData = onboardingData ?? {};
  // Default the input to the current client owner's URL on every open so
  // reassigning Ben → Leslie auto-refreshes the admin view, even if the org
  // still has the previous owner's URL persisted in onboardingData. The admin
  // can still overwrite the value before saving if they need a custom link.
  const [meetingLink, setMeetingLink] = useState(owner.strategyMeetingUrl);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  // Optimistic local state — updated immediately after save so UI reflects changes
  // without waiting for GQL refetch (GQL may lag behind Remi REST PUT)
  const [localCompleted, setLocalCompleted] = useState<boolean | null>(null);
  const [localScheduledLink, setLocalScheduledLink] = useState<string | null>(null);

  const savedLink = localScheduledLink ?? strategyMeetingLink;
  const isCompleted = localCompleted !== null ? localCompleted : strategyCallData?.completed === true;
  const isScheduled = Boolean(savedLink);
  const isAutoPopulated = Boolean(strategyAutoPopulatedAt);

  const handleSchedule = async () => {
    if (!selectedOrg?.id || !meetingLink.trim()) return;

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          onboardingData: {
            ...baseOnboardingData,
            crash_course: {
              meeting_link: meetingLink.trim(),
              completed: false,
            },
          },
        } as Partial<Organization>,
      });

      setLocalScheduledLink(meetingLink.trim());
      setLocalCompleted(false);
      showToast({
        message: 'Strategy call scheduled! Banner will now show to the client.',
        variant: 'success',
      });

      onRefresh?.();
    } catch (error) {
      console.error('Error scheduling strategy call:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to schedule strategy call',
        variant: 'error',
      });
    }
  };

  const handleCompletedToggle = () => {
    if (!isCompleted) {
      // Show confirmation dialog before marking as completed
      setShowConfirmDialog(true);
    } else {
      // Directly toggle off (no confirmation needed)
      handleConfirmCompleted(false);
    }
  };

  const handleConfirmCompleted = async (completed: boolean = true) => {
    if (!selectedOrg?.id) return;

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          onboardingData: {
            ...baseOnboardingData,
            crash_course: {
              meeting_link: strategyMeetingLink || '',
              completed,
              ...(completed && { completed_at: new Date().toISOString() }),
            },
          },
        } as Partial<Organization>,
      });

      setLocalCompleted(completed);
      showToast({
        message: completed
          ? 'Strategy call marked as complete! Banner will disappear.'
          : 'Strategy call marked as incomplete. Banner will show again.',
        variant: 'success',
      });

      onRefresh?.();
    } catch (error) {
      console.error('Error updating strategy call status:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update strategy call status',
        variant: 'error',
      });
    }
    setShowConfirmDialog(false);
  };

  return (
    <div className="space-y-4">
      {/* Strategy Call Status - Only show if scheduled */}
      {isScheduled && (
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-lg p-4 border border-teal-200 dark:border-teal-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2
                className={`w-5 h-5 ${isCompleted ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}
              />
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Strategy Call Status</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {isCompleted
                    ? 'Strategy call has been completed'
                    : isAutoPopulated
                      ? 'Strategy call banner auto-shown after onboarding - visible to client'
                      : 'Strategy call is scheduled - banner is visible to client'}
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isCompleted}
                onChange={handleCompletedToggle}
                disabled={isPending}
                className="w-4 h-4 text-teal-600 border-gray-300 dark:border-white/15 rounded focus:ring-teal-500 cursor-pointer"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Mark as Completed</span>
            </label>
          </div>
        </div>
      )}

      {/* Schedule section - only show if not scheduled yet */}
      {!isScheduled && (
        <div className="space-y-4 bg-gray-50 dark:bg-white/5 rounded-lg p-4 border border-gray-200 dark:border-white/15">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Enter a meeting link to schedule a strategy call. A banner will show to the client
            prompting them to book a session.
          </p>
          <Form.Control>
            <Form.Label>Meeting Link</Form.Label>
            <Input
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://meetings.hubspot.com/..."
            />
          </Form.Control>
          <Button
            variant="primary"
            onClick={handleSchedule}
            isLoading={isPending}
            disabled={!meetingLink.trim()}
          >
            Schedule Strategy Call
          </Button>
        </div>
      )}

      {/* Show meeting link if scheduled — follow the org's current client
          owner, not the (possibly stale) URL saved when the call was first
          scheduled, so reassigning Ben → Leslie auto-refreshes the admin view. */}
      {isScheduled && (
        <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 border border-gray-200 dark:border-white/15">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Meeting Link</p>
          <a
            href={owner.strategyMeetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-teal-600 hover:underline break-all"
          >
            {owner.strategyMeetingUrl}
          </a>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleConfirmCompleted(true)}
        title="Mark Strategy Call as Completed"
        message="Are you sure you want to mark this strategy call as completed? This will hide the 'Schedule Your Strategy Call' banner for the client."
        confirmLabel="Mark as Completed"
        cancelLabel="Cancel"
        variant="info"
        isLoading={isPending}
      />
    </div>
  );
};
